import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, TrendingUpIcon, AwardIcon, DollarSignIcon } from 'lucide-react';
import Button from '../components/Button';
import MetricCard from '../components/MetricCard';
import CaseStudyCard from '../components/CaseStudyCard';
import SkillTag from '../components/SkillTag';
import ScrollToSection from '../components/ScrollToSection';
const HomePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  // Add smooth scrolling for hash links on page load
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          window.scrollTo({
            top: element.offsetTop - 80,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
    // Simulate content loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  // Add animation to elements when they come into view
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
    const handleIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          observer.unobserve(entry.target);
        }
      });
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  const caseStudies = [{
    id: '1',
    title: 'AI-Powered Onboarding Redesign',
    description: 'Led the redesign of our customer onboarding experience at my Stealth Startup, implementing personalized flows based on user behavior data and AI-driven recommendations.',
    metrics: ['Increased user retention by 37% within 3 months', 'Drove $1.8M in Annual Recurring Revenue (14% growth)', 'Reduced customer acquisition cost by 22% ($16 per user)'],
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    tags: ['User Experience', 'Analytics', 'AI']
  }, {
    id: '2',
    title: 'Enterprise Payment System Overhaul',
    description: 'Executed a strategic rebuild of our payment infrastructure during my time at InnovateTech, replacing error-prone manual processes with an automated validation system.',
    metrics: ['Reduced financial discrepancies by $1.1M annually (32% improvement)', 'Decreased payment processing time by 13 days (55% faster)', 'Improved payment accuracy to 99.6% (from 78%)'],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    tags: ['Fintech', 'Process Optimization']
  }, {
    id: '3',
    title: 'Healthcare Support Experience Transformation',
    description: 'Redesigned the patient support experience at HealthTech Solutions, implementing an AI-powered triage system that reduced wait times and improved first-contact resolution rates.',
    metrics: ['Increased CSAT from 67% to 92% within two quarters', 'Improved NPS score from +12 to +26 (healthcare industry average: +18)', 'Reduced support ticket volume by 23% (saving $320K annually)'],
    image: 'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    tags: ['Customer Experience', 'AI', 'Healthcare']
  }];
  const technicalSkills = ['Jira', 'Monday.com', 'SQL', 'Product Roadmapping', 'Agile/Scrum', 'A/B Testing'];
  const analyticalSkills = ['Google Analytics', 'Amplitude', 'Mixpanel', 'Data Visualization', 'User Research'];
  const aiSkills = ['LangChain', 'OpenAI API', 'Vector Databases', 'AI Product Strategy', 'LLM Integration'];
  const softSkills = ['Leadership', 'Strategy', 'User Empathy', 'Stakeholder Management', 'Cross-functional Collaboration'];
  const domainExpertise = ['Healthcare', 'FinTech', 'Enterprise SaaS'];
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-secondary-50" data-id="element-368">
        <div className="animate-pulse flex flex-col items-center" data-id="element-369">
          <div className="h-12 w-12 rounded-full bg-primary-300 mb-4" data-id="element-370"></div>
          <div className="h-4 w-48 bg-gray-200 rounded mb-2" data-id="element-371"></div>
          <div className="h-3 w-32 bg-gray-200 rounded" data-id="element-372"></div>
        </div>
      </div>;
  }
  return <div className="bg-secondary-50 w-full" data-id="element-373">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-primary-900 text-white" data-id="element-374">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-375">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-376">
            <div data-id="element-377">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight animate-fade-in" data-id="element-378">
                Building Products That{' '}
                <span className="text-primary-200" data-id="element-379">Matter</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-gray-200 animate-fade-in" style={{
              animationDelay: '0.2s'
            }} data-id="element-380">
                Product leader with 8+ years of experience crafting data-driven
                solutions that solve real problems and deliver measurable
                business impact.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 animate-fade-in" style={{
              animationDelay: '0.4s'
            }} data-id="element-381">
                <ScrollToSection to="#case-studies" data-id="element-382">
                  <Button variant="secondary" size="lg" data-id="element-383">
                    View My Work
                  </Button>
                </ScrollToSection>
                <Button href="/contact" variant="outline" size="lg" className="bg-transparent text-white border-white/30 hover:bg-white/10" data-id="element-384">
                  Get In Touch
                </Button>
              </div>
            </div>
            <div className="hidden md:block animate-fade-in" style={{
            animationDelay: '0.3s'
          }} data-id="element-385">
              <div className="relative" data-id="element-386">
                <div className="absolute -inset-0.5 bg-primary-400 rounded-lg blur opacity-30" data-id="element-387"></div>
                <img src="https://image2url.com/images/1758821830824-25510891-afe9-48c7-a891-ccdf2f333e3b.jpg" alt="Professional Headshot of Mohit Pole" className="relative rounded-lg shadow-xl w-full object-cover" loading="eager" data-id="element-388" />
              </div>
            </div>
          </div>
        </div>
        <div className="h-16 bg-gradient-to-b from-transparent to-white" data-id="element-389"></div>
      </section>
      {/* Key Metrics Section */}
      <section className="py-16 bg-white" data-id="element-390">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-391">
          <div className="text-center mb-12 animate-on-scroll" data-id="element-392">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800" data-id="element-393">
              Proven Results That Drive Business Growth
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-394">
              At my current Stealth Startup and previous roles, I've
              consistently delivered measurable outcomes.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-395">
            <div className="animate-on-scroll" data-id="element-396">
              <MetricCard title="User Retention" value="+37%" percentage="QoQ improvement" timeframe="Q2 2023" description="Implemented behavioral analysis and personalized onboarding at Stealth Startup" icon={<TrendingUpIcon className="h-6 w-6" data-id="element-398" />} data-id="element-397" />
            </div>
            <div className="animate-on-scroll" style={{
            animationDelay: '0.15s'
          }} data-id="element-399">
              <MetricCard title="Revenue Impact" value="$1.8M" percentage="+14% ARR" timeframe="FY 2023" description="Drove revenue growth through targeted feature optimization and enhanced user journeys" icon={<DollarSignIcon className="h-6 w-6" data-id="element-401" />} data-id="element-400" />
            </div>
            <div className="animate-on-scroll" style={{
            animationDelay: '0.3s'
          }} data-id="element-402">
              <MetricCard title="Customer Satisfaction" value="+25%" percentage="92% CSAT" timeframe="Last 12 months" description="Redesigned core workflows to eliminate friction points and improve task completion rates" icon={<AwardIcon className="h-6 w-6" data-id="element-404" />} data-id="element-403" />
            </div>
          </div>
        </div>
      </section>
      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-secondary-50" data-id="element-405">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-406">
          <div className="text-center mb-16 animate-on-scroll" data-id="element-407">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800" data-id="element-408">
              Strategic Product Initiatives
            </h2>
            <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-409">
              A sample of how I've tackled complex business challenges through
              user-centered design, data-driven decisions, and strategic
              thinking.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-410">
            {caseStudies.map((study, index) => <div key={study.id} className="animate-on-scroll" style={{
            animationDelay: `${index * 100}ms`
          }} data-id="element-411">
                <CaseStudyCard id={study.id} title={study.title} description={study.description} metrics={study.metrics} image={study.image} tags={study.tags} data-id="element-412" />
              </div>)}
          </div>
          <div className="mt-16 text-center animate-on-scroll" data-id="element-413">
            <Button href="/about" variant="primary" size="lg" data-id="element-414">
              Explore My Experience <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-415" />
            </Button>
          </div>
        </div>
      </section>
      {/* Skills Section */}
      <section className="py-24 bg-white" data-id="element-416">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-417">
          <div className="text-center mb-16 animate-on-scroll" data-id="element-418">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-800" data-id="element-419">
              Product Leadership Toolkit
            </h2>
            <p className="mt-6 text-xl text-secondary-600 max-w-3xl mx-auto" data-id="element-420">
              My skill set combines technical knowledge, analytical
              capabilities, and strategic leadership—developed over 8 years in
              product management.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-10" data-id="element-421">
            <div className="bg-secondary-50 rounded-2xl p-8 shadow-card animate-on-scroll" data-id="element-422">
              <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-423">
                Technical Proficiency
              </h3>
              <div className="flex flex-wrap gap-3 mb-10" data-id="element-424">
                {technicalSkills.map((skill, index) => <SkillTag key={index} name={skill} category="technical" data-id="element-425" />)}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-426">
                Data & Analytics
              </h3>
              <div className="flex flex-wrap gap-3 mb-10" data-id="element-427">
                {analyticalSkills.map((skill, index) => <SkillTag key={index} name={skill} category="analytical" data-id="element-428" />)}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-429">
                AI Expertise
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-430">
                {aiSkills.map((skill, index) => <SkillTag key={index} name={skill} category="ai" data-id="element-431" />)}
              </div>
            </div>
            <div className="bg-secondary-50 rounded-2xl p-8 shadow-card animate-on-scroll" style={{
            animationDelay: '100ms'
          }} data-id="element-432">
              <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-433">
                Leadership & Strategy
              </h3>
              <div className="flex flex-wrap gap-3 mb-10" data-id="element-434">
                {softSkills.map((skill, index) => <SkillTag key={index} name={skill} category="soft" data-id="element-435" />)}
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-436">
                Industry Knowledge
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-437">
                {domainExpertise.map((skill, index) => <SkillTag key={index} name={skill} category="domain" data-id="element-438" />)}
              </div>
              <div className="mt-10 p-8 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200" data-id="element-439">
                <h4 className="text-lg font-semibold text-primary-800 mb-3" data-id="element-440">
                  My Product Philosophy
                </h4>
                <p className="text-primary-700" data-id="element-441">
                  The best products solve real problems at the intersection of
                  user needs, business objectives, and technical feasibility. I
                  thrive in finding that balance to deliver exceptional
                  outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-primary-800 text-white" data-id="element-442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-443">
          <h2 className="text-2xl md:text-3xl font-bold" data-id="element-444">
            Looking for a Product Leader to Drive Results?
          </h2>
          <p className="mt-6 text-lg text-gray-200 max-w-2xl mx-auto" data-id="element-445">
            I'm currently exploring new opportunities where I can leverage my
            expertise in product strategy, AI integration, and user experience
            design.
          </p>
          <div className="mt-8" data-id="element-446">
            <Button href="/contact" variant="secondary" size="lg" data-id="element-447">
              Let's Connect
            </Button>
          </div>
        </div>
      </section>
    </div>;
};
export default HomePage;